// Pick a person or agent by searching the server's directory, never by typing a name blind: a bare
// "parishi" once invited a stranger with the same name. Results show name, handle and picture, and the
// pick is confirmed before anything is sent.
import React, { useEffect, useRef, useState } from 'react';
import { View, Pressable, ActivityIndicator, StyleSheet, Alert, Platform } from 'react-native';
import { Text, TextInput } from './Text';
import { DirectoryUser, searchUsers, lookupUser, useIsAgent, localpart, isEmail } from '../matrix';
import { Avatar } from './Avatar';
import { radius, makeStyles, useTheme } from '../theme';

export function UserPicker({ onPick, onPickEmail, confirmLabel = 'Invite', exclude = [], busy = false, placeholder = 'Search by name' }: {
  onPick: (u: DirectoryUser) => void;
  /** When set, a typed email address offers an invite by email (someone with no account yet). */
  onPickEmail?: (email: string) => void;
  /** The verb on the confirm button: Invite, Start chat… */
  confirmLabel?: string;
  /** Ids to hide from results (people already in the room). */
  exclude?: string[];
  busy?: boolean; placeholder?: string;
}) {
  const theme = useTheme(); const s = useStyles(); const isAgent = useIsAgent();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<DirectoryUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim(); const my = ++seq.current;
    if (term.length < 2 || isEmail(term)) { setHits([]); setSearching(false); setErr(null); return; }
    setSearching(true); setErr(null);
    const t = setTimeout(async () => {
      try {
        let found = await searchUsers(term);
        // A full Matrix id that the directory does not know yet (no shared room) can still be looked up.
        if (!found.length && /^@[^:\s]+:[^\s]+$/.test(term)) { const u = await lookupUser(term); if (u) found = [u]; }
        if (my === seq.current) setHits(found);
      } catch (e: any) { if (my === seq.current) setErr(e?.message || 'Search failed'); }
      finally { if (my === seq.current) setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const confirm = (u: DirectoryUser) => {
    const go = () => { setQ(''); setHits([]); onPick(u); };
    Alert.alert(`${confirmLabel} ${u.name}?`, u.id, [{ text: 'Cancel', style: 'cancel' }, { text: confirmLabel, onPress: go }]);
  };

  const term = q.trim();
  const shown = hits.filter((u) => !exclude.includes(u.id));
  const emailMode = !!onPickEmail && isEmail(term);
  const confirmEmail = () => {
    const go = () => { setQ(''); onPickEmail?.(term.toLowerCase()); };
    Alert.alert(`Invite ${term}?`, 'They get an email with a sign-up link and join this room once they sign up.', [{ text: 'Cancel', style: 'cancel' }, { text: 'Send invite', onPress: go }]);
  };
  return (
    <View>
      <View style={s.box} {...boxAttrs}>
        <TextInput style={s.input} value={q} onChangeText={setQ} placeholder={placeholder} placeholderTextColor={theme.faint}
          autoCapitalize="none" autoCorrect={false} editable={!busy} accessibilityLabel="Search people" />
        {(searching || busy) && <ActivityIndicator color={theme.accent} size="small" />}
      </View>
      {emailMode ? (
        <Pressable onPress={confirmEmail} disabled={busy} style={({ pressed }) => [s.row, pressed && { backgroundColor: theme.surface2 }]} accessibilityRole="button" accessibilityLabel={`Invite ${term} by email`}>
          <View style={s.mailIcon}><Text style={s.mailGlyph}>✉</Text></View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.name} numberOfLines={1}>Invite by email</Text>
            <Text style={s.id} numberOfLines={1}>{term} · they sign up, then join</Text>
          </View>
          <Text style={s.chev}>›</Text>
        </Pressable>
      ) : term.length < 2 ? (
        <Text style={s.hint}>Type a name. You can pick people and agents you already share a room with.{onPickEmail ? ' Or type an email address to invite someone new.' : ''}</Text>
      ) : err ? (
        <Text style={[s.hint, { color: theme.deny }]}>{err}</Text>
      ) : shown.length === 0 && hits.length > 0 && !searching ? (
        <Text style={s.hint}>Everyone matching “{term}” is already here.</Text>
      ) : shown.length === 0 && !searching ? (
        <Text style={s.hint}>No one found for “{term}”. They may need to sign up first, or you don't share a room with them yet.</Text>
      ) : shown.map((u) => (
        <Pressable key={u.id} onPress={() => confirm(u)} disabled={busy} style={({ pressed }) => [s.row, pressed && { backgroundColor: theme.surface2 }]}
          accessibilityRole="button" accessibilityLabel={`${confirmLabel} ${u.name}`}>
          <Avatar name={u.name} mxc={u.avatarMxc} kind={isAgent(u.id) ? 'agent' : 'human'} size={34} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.name} numberOfLines={1}>{u.name}</Text>
            <Text style={s.id} numberOfLines={1}>@{localpart(u.id)}{isAgent(u.id) ? ' · agent' : ''}</Text>
          </View>
          <Text style={s.chev}>›</Text>
        </Pressable>
      ))}
    </View>
  );
}

// Web: the focus ring belongs on the box, not on the bare field inside it (webChrome.ts styles [data-box]).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const boxAttrs: any = Platform.OS === 'web' ? { dataSet: { box: 'true' } } : {};

const useStyles = makeStyles((theme) => StyleSheet.create({
  box: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, paddingHorizontal: 12 },
  input: { flex: 1, color: theme.ink, fontSize: 15, paddingVertical: 10 },
  hint: { color: theme.faint, fontSize: 12.5, marginTop: 8, lineHeight: 17 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, paddingHorizontal: 6, marginTop: 4, borderRadius: radius.md },
  name: { color: theme.ink, fontSize: 15, fontWeight: '600' },
  id: { color: theme.faint, fontSize: 12, fontFamily: theme.mono, marginTop: 1 },
  chev: { color: theme.faint, fontSize: 20 },
  mailIcon: { width: 34, height: 34, borderRadius: 10, backgroundColor: theme.accentBg, borderWidth: 1, borderColor: theme.accentBorder, alignItems: 'center', justifyContent: 'center' },
  mailGlyph: { color: theme.accent, fontSize: 16 },
}));
